import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { ConfirmDialog } from "../components/ConfirmDialog";
import { TaskCollectionItem } from "../components/TaskCollectionItem";
import { ApiError, type FamilyApi } from "../lib/api";
import type { ParentMember, SessionResponse, TaskOccurrence, TaskStatus, TaskTemplate } from "../lib/types";

interface AllTasksPageProps { api: FamilyApi; offline: boolean; session?: Extract<SessionResponse, { authenticated: true }> }
interface Data { members: ParentMember[]; templates: TaskTemplate[]; tasks: TaskOccurrence[] }

export function AllTasksPage({ api, offline, session }: AllTasksPageProps) {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<TaskOccurrence | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [members, templates, occurrences] = await Promise.all([
        api.getMembers(), api.getTemplates(), api.getOccurrences(),
      ]);
      setData({ members: members.data, templates: templates.data, tasks: occurrences.data });
    } catch (caught) { setError(messageFor(caught)); }
  }, [api]);

  useEffect(() => { void load(); }, [load]);

  const query = params.get("q") ?? "";
  const assignee = params.get("assignee") ?? "ALL";
  const status = params.get("status") ?? "ALL";
  const filtered = useMemo(() => data?.tasks.filter((task) => {
    const assignmentKey = task.assignment.type === "HOUSEHOLD" ? "HOUSEHOLD" : task.assignment.memberId;
    return task.title.toLocaleLowerCase("zh-TW").includes(query.trim().toLocaleLowerCase("zh-TW")) &&
      (assignee === "ALL" || assignmentKey === assignee) && (status === "ALL" || task.status === status);
  }) ?? [], [assignee, data, query, status]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (!value || value === "ALL") next.delete(key); else next.set(key, value);
    setParams(next, { replace: true });
  };

  const archive = async () => {
    if (!archiveTarget || !data) return;
    const target = archiveTarget;
    const template = data.templates.find((item) => item.templateId === target.templateId);
    if (!template) { setError("找不到這筆待辦的原始資料，請重新整理。"); return; }
    setBusyId(target.occurrenceId);
    try {
      await api.archiveTemplate(template.templateId, template.version, target.version);
      setData({ ...data, templates: data.templates.filter((item) => item.templateId !== template.templateId), tasks: data.tasks.filter((item) => item.occurrenceId !== target.occurrenceId) });
      setNotice(`已封存「${target.title}」`);
      setArchiveTarget(null);
    } catch (caught) { setError(messageFor(caught)); }
    finally { setBusyId(null); }
  };

  const move = async (task: TaskOccurrence, direction: -1 | 1) => {
    if (!data) return;
    const bucket = bucketTasks(data.tasks, task);
    const index = bucket.findIndex((item) => item.occurrenceId === task.occurrenceId);
    const swapWith = bucket[index + direction];
    if (index < 0 || !swapWith) return;
    [bucket[index], bucket[index + direction]] = [swapWith, task];
    setBusyId(task.occurrenceId);
    try {
      await api.reorderOccurrences({ assignee: task.assignment, scheduledDate: task.scheduledDate, items: bucket.map((item) => ({ occurrenceId: item.occurrenceId, expectedVersion: item.version })) });
      await load();
    } catch (caught) { setError(messageFor(caught)); }
    finally { setBusyId(null); }
  };

  return (
    <section className="page-stack" aria-labelledby="tasks-title">
      <header className="page-heading page-heading-row"><div><h1 id="tasks-title">所有待辦</h1><p>搜尋、安排與整理一家人的任務。</p></div><Link className="button button-primary compact-add" to="/tasks/new">＋ 新增</Link></header>
      <div className="task-filters">
        <label className="search-field"><span className="sr-only">搜尋待辦</span><input type="search" aria-label="搜尋待辦" value={query} onChange={(event) => updateParam("q", event.currentTarget.value)} placeholder="搜尋待辦" /></label>
        <label><span>負責人</span><select value={assignee} onChange={(event) => updateParam("assignee", event.currentTarget.value)}><option value="ALL">全部</option><option value="HOUSEHOLD">全家</option>{data?.members.map((member) => <option key={member.memberId} value={member.memberId}>{member.displayName}</option>)}</select></label>
        <label><span>狀態</span><select value={status} onChange={(event) => updateParam("status", event.currentTarget.value)}><option value="ALL">全部</option>{(["OPEN", "COMPLETED", "SKIPPED", "CANCELLED"] satisfies TaskStatus[]).map((value) => <option key={value} value={value}>{statusText(value)}</option>)}</select></label>
      </div>
      {offline && data ? <p className="stale-note">目前顯示離線資料，修改功能已停用。</p> : null}
      {error ? <div className="notice notice-error" role="alert">{error}<button type="button" onClick={() => void load()}>重新整理</button></div> : null}
      {notice ? <p className="notice notice-success" role="status">{notice}</p> : null}
      {!data ? <div className="task-skeleton" aria-label="正在載入所有待辦" aria-busy="true"><span /><span /><span /></div> : null}
      {data && filtered.length === 0 ? <div className="empty-state" role="status"><span className="empty-symbol" aria-hidden="true">✓</span><h2>沒有符合的待辦</h2><p>換個篩選條件，或新增一件事。</p></div> : null}
      {data && filtered.length ? <ul className="collection-list">{filtered.map((task) => {
        const bucket = bucketTasks(data.tasks, task);
        const index = bucket.findIndex((item) => item.occurrenceId === task.occurrenceId);
        const memberId = task.assignment.type === "MEMBER" ? task.assignment.memberId : null;
        const canManage = !session || session.member.role === "PARENT" || memberId === session.member.memberId;
        return <TaskCollectionItem key={task.occurrenceId} task={task} member={data.members.find((member) => member.memberId === memberId)} offline={offline} busy={busyId === task.occurrenceId} canManage={canManage} canMoveUp={index > 0} canMoveDown={index >= 0 && index < bucket.length - 1} onArchive={() => setArchiveTarget(task)} onMove={(direction) => void move(task, direction)} />;
      })}</ul> : null}
      {archiveTarget ? <ConfirmDialog title="封存待辦？" description={`「${archiveTarget.title}」會從清單移除，既有紀錄仍會保留。`} confirmLabel="確認封存" destructive busy={busyId === archiveTarget.occurrenceId} onCancel={() => setArchiveTarget(null)} onConfirm={() => void archive()} /> : null}
    </section>
  );
}

function bucketTasks(tasks: TaskOccurrence[], target: TaskOccurrence) { const key = assignmentKey(target); return tasks.filter((task) => task.status === "OPEN" && assignmentKey(task) === key && task.scheduledDate === target.scheduledDate).sort((a, b) => a.sortOrder - b.sortOrder || a.occurrenceId.localeCompare(b.occurrenceId)); }
function assignmentKey(task: TaskOccurrence) { return task.assignment.type === "HOUSEHOLD" ? "HOUSEHOLD" : task.assignment.memberId; }
function statusText(status: TaskStatus) { return { OPEN: "待完成", COMPLETED: "已完成", SKIPPED: "已略過", CANCELLED: "已取消" }[status]; }
function messageFor(error: unknown) { return error instanceof ApiError ? error.message : "待辦暫時無法更新，請再試一次。"; }
